import { Box, CircularProgress } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getBluonAIToken } from '@config/api/masterMechanic';
import { useAppSelector } from '@hooks/state';
import useGoogleTagManager from '@hooks/useGoogleTagManager';
import { getStoreInfoData } from '@state/storeInfo/selectors';
import { assembleMasterMechanicGTMEvent } from '@utils/GoogleTagManager';

import './styles.scss';

const MM_BASE_URL = process.env?.REACT_APP_MASTER_MECHANIC_IFRAME_URL;
const MM_ORIGIN = 'live';
export const MM_ROUTE = '/master-mechanic';

const MasterMechanic = () => {
  const { pathname, search } = useLocation();
  const { setGTMEvent } = useGoogleTagManager();
  const isCorrectRoute = pathname === MM_ROUTE;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isFirstMount = useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  const [chatbotReady, setChatbotReady] = useState(false);
  const [tokenChatBot, setTokenChatBot] = useState<string | null>(null);

  const { modelId, operatingMode } = useMemo(() => {
    const searchParams = new URLSearchParams(search);

    return {
      modelId: searchParams.get('modelId') || null,
      operatingMode: searchParams.get('operating_mode') || null,
    };
  }, [search]);

  const { supplierId } = useAppSelector((state) => ({
    supplierId: getStoreInfoData(state)?.id,
  }));

  useEffect(() => {
    if (isCorrectRoute && supplierId) {
      const snowplowData = {
        ...(supplierId && { Supplier_id: supplierId }),
        origin: MM_ORIGIN,
      };
      setGTMEvent(assembleMasterMechanicGTMEvent(snowplowData));
    }
  }, [isCorrectRoute, supplierId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === MM_BASE_URL) {
        const data = event.data as { type?: string };
        if (data?.type === 'chatbot-ready') setChatbotReady(true);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!isCorrectRoute || !isFirstMount.current) return;

    isFirstMount.current = false;

    const setupToken = async () => {
      setIsLoading(true);
      try {
        const response = await getBluonAIToken();
        const token = response.data.token;
        setTokenChatBot(token);
      } finally {
        setIsLoading(false);
      }
    };

    setupToken();
  }, [isCorrectRoute]);

  useEffect(() => {
    if (tokenChatBot && iframeRef.current?.contentWindow && chatbotReady)
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'auth',
          token: tokenChatBot,
          timestamp: Date.now(),
        },
        { targetOrigin: MM_BASE_URL },
      );
  }, [tokenChatBot, chatbotReady]);

  const mmUrl = useMemo(() => {
    if (!tokenChatBot) return '';

    const url = new URL(`${MM_BASE_URL}/chat`);
    url.searchParams.append('source', MM_ORIGIN);

    if (modelId) url.searchParams.append('model_id', modelId);
    if (operatingMode) url.searchParams.append('operating_mode', operatingMode);

    return url.toString();
  }, [tokenChatBot, modelId, operatingMode]);

  return (
    <Box
      className='mm-container'
      style={{ display: isCorrectRoute ? 'flex' : 'none' }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <iframe
          ref={iframeRef}
          className='mm-iframe'
          src={mmUrl}
          frameBorder='0'
          allow='microphone *; clipboard-write'
        />
      )}
    </Box>
  );
};

export default MasterMechanic;
